<script setup>
import { ref, computed, onMounted } from 'vue';
import { DEFAULT_REDIRECT_URL } from 'dashboard/constants/globals';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { verifyPasswordToken } from '../../../api/auth';
import SiteFooter from '../SiteFooter.vue';

const props = defineProps({
  confirmationToken: {
    type: String,
    default: '',
  },
});

const status = ref('pending');
const user = ref(null);

const accountId = computed(
  () => user.value?.account_id || user.value?.accounts?.[0]?.id
);

const dashboardUrl = computed(() => {
  if (accountId.value)
    return frontendURL(`accounts/${accountId.value}/dashboard`);
  return DEFAULT_REDIRECT_URL;
});

const goToDashboard = () => {
  // Flag the first-run onboarding tour so the dashboard shows it once as an overlay.
  try { localStorage.setItem('cc_show_onboarding', '1'); } catch (e) {}
  window.location = dashboardUrl.value;
};

const goToSignIn = () => {
  window.location = frontendURL('login');
};

onMounted(async () => {
  if (!props.confirmationToken) {
    status.value = 'error';
    return;
  }
  try {
    const data = await verifyPasswordToken({
      confirmationToken: props.confirmationToken,
    });
    if (data) {
      user.value = data;
      status.value = 'success';
    } else {
      status.value = 'error';
    }
  } catch {
    status.value = 'error';
  }
});
</script>

<template>
  <div class="cc-confirm-page">
    <div class="cc-confirm-noise" />

    <header class="cc-confirm-header">
      <div class="cc-brand-mark">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="38"
          height="38"
          viewBox="0 0 96 92"
          class="cc-brand-icon"
        >
          <defs>
            <linearGradient id="goldgrad-cf" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#d8b86a" />
              <stop offset="0.5" stop-color="#a8842f" />
              <stop offset="1" stop-color="#7a5a14" />
            </linearGradient>
            <clipPath id="clipover-cf">
              <rect x="38" y="44" width="20" height="22" />
            </clipPath>
          </defs>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(7,11)"
            fill="none"
            stroke="#0d9488"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(7,11)" fill="#0d9488">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 32 36 L 37 47 L 20 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(41,30)"
            fill="none"
            stroke="url(#goldgrad-cf)"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(41,30)" fill="#a8842f">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <g clip-path="url(#clipover-cf)">
            <path
              d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
              transform="translate(7,11)"
              fill="none"
              stroke="#0d9488"
              stroke-width="7"
              stroke-linejoin="round"
            />
          </g>
        </svg>
        <div class="cc-brand-text">
          <div class="cc-wordmark">
            <span class="cc-wordmark-part cc-wordmark-creator">Creator</span><span class="cc-wordmark-dash">-</span><span class="cc-wordmark-part cc-wordmark-chat">Chat</span><span class="cc-wordmark-tld">.com</span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="90"
            height="2"
            viewBox="0 0 90 2"
            class="cc-brand-divider"
          >
            <defs>
              <linearGradient id="tealfade-cf" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#0d9488" stop-opacity="0" />
                <stop offset="20%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="80%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="100%" stop-color="#0d9488" stop-opacity="0" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="90" height="2" fill="url(#tealfade-cf)" />
          </svg>
          <div class="cc-tagline">Conversations · Tailored</div>
        </div>
      </div>
    </header>

    <main class="cc-confirm-main">
      <!-- PENDING -->
      <div v-if="status === 'pending'" class="cc-confirm-card cc-confirm-pending">
        <div class="cc-spinner-wrap">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0f766e" stroke-width="2.5" stroke-linecap="round" class="cc-spinner">
            <line x1="12" y1="2" x2="12" y2="6" />
            <line x1="12" y1="18" x2="12" y2="22" />
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
            <line x1="2" y1="12" x2="6" y2="12" />
            <line x1="18" y1="12" x2="22" y2="12" />
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
          </svg>
        </div>
        <p class="cc-pending-text">Activating your account…</p>
      </div>

      <!-- SUCCESS — minimal confirmation. The "what to do next" onboarding
           lives in the welcome email, so this page is just: animated check,
           confirmation line, and a single CTA into the dashboard. -->
      <div v-else-if="status === 'success'" class="cc-confirm-card cc-confirm-done">
        <div class="cc-check-badge">
          <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
            <circle
              class="cc-check-ring"
              cx="26" cy="26" r="24"
              fill="none" stroke="#0d9488" stroke-width="3"
            />
            <path
              class="cc-check-tick"
              d="M15 27 l7 7 l15 -16"
              fill="none" stroke="#0d9488" stroke-width="4"
              stroke-linecap="round" stroke-linejoin="round"
            />
          </svg>
        </div>

        <h1 class="cc-h-display cc-h-display--done">Your account is activated</h1>
        <p class="cc-lede cc-lede--centered">You're all set and ready to go.</p>

        <button type="button" class="cc-btn-primary" @click="goToDashboard">
          Access Dashboard
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>

      <!-- ERROR -->
      <div v-else class="cc-confirm-card cc-confirm-error">
        <div class="cc-error-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#b45309" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h1 class="cc-h-display cc-h-display--error">We couldn't verify your account</h1>
        <p class="cc-lede cc-lede--centered">
          The verification link may have expired or already been used. You can try signing in if your account is already active, or request a new verification email.
        </p>
        <button type="button" class="cc-btn-primary" @click="goToSignIn">
          Go to sign-in
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
    </main>

    <SiteFooter />
  </div>
</template>

<style scoped>
.cc-confirm-page {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: #efeae0;
  color: #0b1414;
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
}
.cc-confirm-noise {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.35;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.4 0 0 0 0 0.35 0 0 0 0 0.25 0 0 0 0.05 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
}

.cc-confirm-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px 36px;
}

.cc-brand-mark { display: flex; align-items: center; }
.cc-brand-icon { display: block; margin-right: 14px; }
.cc-brand-text { line-height: 1; }
.cc-wordmark {
  line-height: 21px;
  white-space: nowrap;
}
.cc-wordmark-part {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  text-transform: uppercase;
  color: #0b1414;
}
.cc-wordmark-creator { font-weight: 400; }
.cc-wordmark-chat { font-weight: 300; }
.cc-wordmark-dash {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  color: #a8842f;
  font-weight: 400;
}
.cc-wordmark-tld {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: lowercase;
  color: #a8842f;
  vertical-align: 8px;
  padding-left: 2px;
}
.cc-brand-divider {
  display: block;
  margin: 6px 0 5px 0;
}
.cc-tagline {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 8.5px;
  font-weight: 500;
  letter-spacing: 2.56px;
  color: rgba(11, 20, 20, 0.5);
  text-transform: uppercase;
  line-height: 1;
}

.cc-confirm-main {
  position: relative;
  z-index: 1;
  flex: 1;
  max-width: 640px;
  width: 100%;
  margin: 50px auto 60px;
  padding: 0 24px;
  box-sizing: border-box;
}

.cc-confirm-card {
  background: #ffffff;
  border: 1px solid #e0d8c5;
  border-radius: 16px;
  padding: 40px 40px 36px;
}

.cc-confirm-pending {
  display: flex;
  align-items: center;
  gap: 14px;
  justify-content: center;
  padding: 50px 36px;
}

.cc-spinner-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(13, 148, 136, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.cc-spinner {
  animation: cc-spin 0.8s linear infinite;
}
@keyframes cc-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.cc-pending-text {
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #2a3434;
  margin: 0;
}

.cc-success-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}
.cc-success-icon {
  width: 54px;
  height: 54px;
  border-radius: 14px;
  background: #0d9488;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Minimal success state (post-verify): centered animated check + CTA. */
.cc-confirm-done {
  text-align: center;
}
.cc-check-badge {
  width: 88px;
  height: 88px;
  margin: 6px auto 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(13, 148, 136, 0.1);
  animation: cc-badge-pop 0.45s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes cc-badge-pop {
  0%   { transform: scale(0.4); opacity: 0; }
  60%  { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); }
}
.cc-check-ring {
  stroke-dasharray: 151;   /* 2·π·24 ≈ 150.8 — full ring draw-in */
  stroke-dashoffset: 151;
  animation: cc-check-draw 0.5s 0.1s ease-out forwards;
}
.cc-check-tick {
  stroke-dasharray: 40;
  stroke-dashoffset: 40;
  animation: cc-check-draw 0.35s 0.5s ease-out forwards;
}
@keyframes cc-check-draw {
  to { stroke-dashoffset: 0; }
}
.cc-h-display--done {
  text-align: center;
  font-size: 24px;
  margin: 0 0 6px;
}
@media (prefers-reduced-motion: reduce) {
  .cc-check-badge { animation: none; }
  .cc-check-ring,
  .cc-check-tick { animation: none; stroke-dashoffset: 0; }
}

.cc-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.cc-pill-free {
  background: rgba(13, 148, 136, 0.22);
  color: #0f766e;
}
.cc-pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.cc-h-display {
  font-family: 'Cinzel', 'Fraunces', 'Times New Roman', Georgia, serif;
  font-weight: 600;
  color: #0b1414;
  letter-spacing: -0.005em;
  font-size: 24px;
  line-height: 1.2;
  margin: 6px 0 0;
}

.cc-lede {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #2a3434;
  line-height: 1.55;
  margin: 0 0 22px;
}
.cc-lede--centered {
  text-align: center;
  color: #6e7570;
  margin: 14px 0 22px;
}

.cc-onboarding {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
}
.cc-onboarding-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  background: #f1ede4;
  border: 1px solid #e0d8c5;
  border-radius: 11px;
  width: 100%;
  text-align: left;
  font-family: inherit;
  color: inherit;
  cursor: pointer;
  transition: all 0.12s;
}
.cc-onboarding-row:hover {
  background: #ffffff;
  border-color: #cfc5ac;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(11, 20, 20, 0.06);
}
.cc-onboarding-row:hover .cc-onboarding-chev {
  stroke: #0f766e;
  transform: translateX(2px);
}
.cc-onboarding-row--active {
  border-color: rgba(13, 148, 136, 0.5);
}
.cc-onboarding-num {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #ffffff;
  border: 1.5px solid #cfc5ac;
  color: #6e7570;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 800;
  flex-shrink: 0;
}
.cc-onboarding-num--active {
  border-color: #0d9488;
  color: #0f766e;
}
.cc-onboarding-body {
  flex: 1;
  min-width: 0;
}
.cc-onboarding-title {
  font-family: 'Inter', sans-serif;
  font-size: 13.5px;
  font-weight: 700;
  color: #0b1414;
  margin-bottom: 2px;
}
.cc-onboarding-desc {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #6e7570;
  line-height: 1.4;
}
.cc-onboarding-chev {
  margin-top: 8px;
  flex-shrink: 0;
}

.cc-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 14px;
  border-radius: 10px;
  border: none;
  background: rgba(31, 42, 42, 0.92);
  color: #f5efe0;
  font-family: 'Inter', sans-serif;
  font-size: 14.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.12s;
  width: 100%;
}
.cc-btn-primary:hover {
  background: rgba(11, 20, 20, 0.98);
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(11, 20, 20, 0.2);
}

.cc-confirm-error {
  text-align: center;
}
.cc-error-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  border-radius: 16px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cc-h-display--error {
  text-align: center;
  font-size: 22px;
  margin: 0 0 6px;
}

@media (max-width: 540px) {
  .cc-confirm-header { padding: 18px 20px; }
  .cc-confirm-main { margin: 30px auto 40px; }
  .cc-confirm-card { padding: 28px 22px; }
  .cc-success-head { gap: 12px; }
  .cc-success-icon { width: 46px; height: 46px; }
  .cc-h-display { font-size: 21px; }
  .cc-tagline { display: none; }
}
</style>
